import React, { useEffect, useState } from 'react';
import {
  useTable,
  usePagination,
  TableInstance,
  UsePaginationInstanceProps,
  UsePaginationState,
} from 'react-table';
import TableBox from '../../common/TableBox';
import ReactTablePagination from '../../../common/ReactTablePagination';
import EmptyState from '../../../common/EmptyState';
import PassesBatchesStatus from './BatchesStatus';
import { Link } from 'react-router-dom';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Box,
  Text,
  Flex,
  Stack,
  Button,
  useColorModeValue,
  useMediaQuery,
  Progress,
  Divider,
} from '@chakra-ui/react';
import { CustomCsvDownloadIcon } from '../../../theme/icons/CustomCsvDownloadIcon';
import { ChevronDownIcon } from '@chakra-ui/icons';
import BatchesCreateMenu from './BatchesCreateMenu';
import BatchesCreateTableProgressDrawer from './BatchesCreateTableProgressDrawer';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';

interface BatchProgress {
  processed?: number;
  total?: number;
}

interface TableData {
  id: string;
  name: string;
  passTemplate: string;
  passTemplateId?: string;
  status: string;
  scheduledAt?: string | null;
  progress?: BatchProgress;
  totalCount?: number;
  completedAt?: string | null;
  csvResults?: string | null;
}

interface TableColumn {
  Header: string;
  accessor: string | ((row: TableData) => string | number | null | undefined);
  id?: string;
  style: {
    minWidth: string;
    width: string;
    textAlign?: string;
  };
}

interface BatchesUpdateTableProps {
  header: string;
  columns: TableColumn[];
  data: TableData[];
  isLoading: boolean;
  pageCount: number;
  total: number;
  fetchData: (params: { pageIndex: number; pageSize: number }) => void;
}

type TableInstanceWithPagination = TableInstance<TableData> &
  UsePaginationInstanceProps<TableData> & {
    state: UsePaginationState<TableData>;
  };

const BatchesUpdateTable = ({
  header,
  columns,
  data,
  isLoading,
  pageCount,
  total,
  fetchData,
}: BatchesUpdateTableProps) => {
  const isReadOnly = useIsReadOnly();
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount: controlledPageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setHiddenColumns,
    state: { pageIndex, pageSize },
  } = useTable<TableData>(
    {
      columns: columns as readonly { Header: string; accessor: string }[],
      data,
      manualPagination: true,
      pageCount,
      initialState: { pageIndex: 0 },
    },
    usePagination
  ) as TableInstanceWithPagination;

  const [isLargerThan2XL] = useMediaQuery('(min-width: 96em)');
  const [isLargerThanXL] = useMediaQuery('(min-width: 80em)');
  const [isLargerThanMD] = useMediaQuery('(min-width: 48em)');
  const [isLargerThanSM] = useMediaQuery('(min-width: 30em)');
  const [isLargerThanS] = useMediaQuery('(min-width: 18em)');
  const [isLargerThanXS] = useMediaQuery('(min-width: 10em)');

  useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  useEffect(() => {
    if (isLargerThan2XL) {
      setHiddenColumns([]);
    } else if (isLargerThanXL) {
      setHiddenColumns(['passTemplate']);
    } else if (isLargerThanMD) {
      setHiddenColumns([
        'passTemplate',
        'totalPasses',
        'progress',
        'totalCount',
      ]);
    } else if (isLargerThanSM) {
      setHiddenColumns([
        'passTemplate',
        'totalPasses',
        'progress',
        'completedAt',
        'totalCount',
      ]);
    } else if (isLargerThanS) {
      setHiddenColumns([
        'passTemplate',
        'totalPasses',
        'progress',
        'completedAt',
        'status',
        'totalCount',
      ]);
    } else {
      setHiddenColumns([
        'passTemplate',
        'totalPasses',
        'progress',
        'completedAt',
        'status',
        'totalCount',
      ]);
    }
  }, [
    isLargerThan2XL,
    isLargerThanXL,
    isLargerThanMD,
    isLargerThanSM,
    isLargerThanS,
    isLargerThanXS,
    setHiddenColumns,
  ]);

  const bgTooltip = useColorModeValue('secondaryDark', '#dddddd');
  const textTooltip = useColorModeValue('#ffffff', 'primaryDark');

  const [collapsedRows, setCollapsedRows] = useState<Set<string>>(new Set());
  const toggleRowCollapse = (id: string) => {
    setCollapsedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <Box textAlign="center">
        <Spinner />
      </Box>
    );
  }

  if (data.length === 0 && !isLoading) {
    return (
      <EmptyState
        heading={`No ${header} Found`}
        text="You haven't created any passes via CSV batches yet."
      >
        {isReadOnly ? (
          <LockedActionButton reason="issue-passes" size="sm">
            New Batch
          </LockedActionButton>
        ) : (
          <Button
            aria-label="New Batch"
            size="sm"
            as={Link}
            to="/passes/batches/create"
          >
            New Batch
          </Button>
        )}
      </EmptyState>
    );
  }

  return (
    <>
      <TableBox>
        <Text textStyle="heading3" mb="16px">
          {header}{' '}
          <Text
            as="span"
            textStyle="headingFamilyMedium"
            fontSize="18px"
            fontWeight="500"
          >
            (
            <Box as="span" color="brand">
              {total}
            </Box>
            )
          </Text>
        </Text>
        <Divider borderColor="primaryBackground" />
        <Table {...getTableProps()}>
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <Th
                    {...column.getHeaderProps()}
                    style={column.style}
                    py={6}
                    fontSize={14}
                    textTransform="none"
                    key={column.id}
                  >
                    {column.render('Header')}
                  </Th>
                ))}
                <Th py={6} fontSize={14} textTransform="none" width="40px">
                  More
                </Th>
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <React.Fragment key={row.id}>
                  <Tr
                    {...row.getRowProps()}
                    _hover={{
                      cursor: 'default',
                    }}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <Td
                          {...cell.getCellProps()}
                          style={cell.column.style}
                          key={cell.column.id}
                        >
                          {cell.column.id === 'status' ? (
                            <PassesBatchesStatus status={cell.value} />
                          ) : cell.column.id === 'progress' ? (
                            cell.row.values.status === 'processing' &&
                            cell.value !== null &&
                            cell.value !== undefined ? (
                              <Progress
                                colorScheme="green"
                                size="lg"
                                value={cell.value}
                              />
                            ) : null
                          ) : cell.column.id === 'csvResults' ? (
                            cell.value ? (
                              <Link to={cell.value}>
                                <CustomCsvDownloadIcon
                                  color="brand"
                                  boxSize={7}
                                />
                              </Link>
                            ) : null
                          ) : cell.column.id === 'name' ? (
                            <Flex align="center">
                              <Text as="span">{cell.value}</Text>
                              {(cell.row.values.status === 'updating' ||
                                cell.row.values.status === 'validating') && (
                                <Box
                                  as="button"
                                  ml={2}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRowCollapse(cell.row.original.id);
                                  }}
                                  aria-label="Toggle row details"
                                >
                                  <ChevronDownIcon
                                    color="gray.500"
                                    transition="transform 0.2s ease"
                                    style={{
                                      transform: collapsedRows.has(
                                        cell.row.original.id
                                      )
                                        ? 'rotate(-180deg)'
                                        : 'rotate(0deg)',
                                    }}
                                    fontSize={22}
                                  />
                                </Box>
                              )}
                            </Flex>
                          ) : cell.column.id === 'passTemplate' &&
                            cell.row.original.passTemplateId ? (
                            <Text
                              as={Link}
                              to={`/pass-templates/edit/${cell.row.original.passTemplateId}`}
                              fontWeight="inherit"
                              _hover={{
                                fontWeight: 'bold',
                              }}
                            >
                              {cell.value}
                            </Text>
                          ) : (
                            cell.render('Cell')
                          )}
                        </Td>
                      );
                    })}
                    <Td width="40px" textAlign="center">
                      <BatchesCreateMenu cell={row} />
                    </Td>
                  </Tr>
                  {row.values.status === 'processing' && (
                    <Tr
                      key={`${row.id}-drawer`}
                      display={
                        collapsedRows.has(row.original.id)
                          ? 'none'
                          : 'table-row'
                      }
                    >
                      <Td
                        colSpan={row.cells.length + 1}
                        p={0}
                        border="none"
                        display={
                          collapsedRows.has(row.original.id)
                            ? 'none'
                            : 'table-cell'
                        }
                      >
                        <Box
                          px={6}
                          py={4}
                          bg="lightGrey2"
                          transition="all 0.4s ease"
                        >
                          <BatchesCreateTableProgressDrawer cell={row} />
                        </Box>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
        {data.length > 0 && (
          <Flex
            mt={6}
            flexDirection={{ base: 'column-reverse', '2xl': 'row' }}
            justifyContent={{ base: 'none', '2xl': 'space-between' }}
          >
            <Stack
              spacing="18px"
              direction={{ base: 'column', sm: 'row' }}
              shouldWrapChildren={true}
            ></Stack>

            <ReactTablePagination
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageCount={pageCount}
              gotoPage={gotoPage}
              nextPage={nextPage}
              previousPage={previousPage}
              setPageSize={setPageSize}
              pageIndex={pageIndex}
              pageSize={pageSize}
            />
          </Flex>
        )}
      </TableBox>
    </>
  );
};

export default BatchesUpdateTable;
